import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Gauge,
  RefreshCw,
  WifiOff,
  X,
} from "lucide-react";
import {
  CONNECTION_STATES,
  getNetworkMessage,
  useNetworkStatus,
} from "../utils/networkDetector";
import API_CONFIG from "../config/api";
import "./NetworkStatusBanner.css";

type ConnectionState =
  | "online"
  | "degraded"
  | "offline"
  | "unreachable";

interface NetworkStatus {
  status: ConnectionState;
  effectiveType?: string;
  downlink?: number | null;
  rtt?: number | null;
  probeLatencyMs?: number | null;
  supported?: boolean;
  isChecking?: boolean;
  recoveryPending?: boolean;
  checkNow: () => Promise<unknown>;
}

interface NetworkStatusBannerProps {
  showMetrics?: boolean;
  floating?: boolean;
}

const PROBLEM_STATES = new Set<ConnectionState>([
  CONNECTION_STATES.DEGRADED,
  CONNECTION_STATES.OFFLINE,
  CONNECTION_STATES.UNREACHABLE,
]);

const RECOVERY_MESSAGE_DURATION_MS = 4_000;

const formatMetric = (value: number | null | undefined, digits = 0) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toFixed(digits)
    : null;

const NetworkStatusBanner: React.FC<NetworkStatusBannerProps> = ({
  showMetrics = true,
  floating = false,
}) => {
  const network = useNetworkStatus({
    enableProbe: true,
    endpoint: API_CONFIG.url("/health"),
  }) as NetworkStatus;
  const [dismissedStatus, setDismissedStatus] = useState<ConnectionState | null>(
    null
  );
  const [showRecovered, setShowRecovered] = useState(false);
  const lastProblemRef = useRef<ConnectionState | null>(
    PROBLEM_STATES.has(network.status) ? network.status : null
  );

  useEffect(() => {
    if (PROBLEM_STATES.has(network.status)) {
      lastProblemRef.current = network.status;
      setDismissedStatus(null);
      setShowRecovered(false);
      return undefined;
    }

    if (
      network.status === CONNECTION_STATES.ONLINE &&
      lastProblemRef.current
    ) {
      lastProblemRef.current = null;
      setShowRecovered(true);
      const timer = window.setTimeout(
        () => setShowRecovered(false),
        RECOVERY_MESSAGE_DURATION_MS
      );
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [network.status]);

  const message = getNetworkMessage(network);
  const metrics = useMemo(() => {
    if (!showMetrics || showRecovered) {
      return [];
    }

    const values: string[] = [];
    if (
      network.supported &&
      network.effectiveType &&
      network.effectiveType !== "unknown"
    ) {
      values.push(`Réseau estimé : ${network.effectiveType.toUpperCase()}`);
    }

    const downlink = formatMetric(network.downlink, 1);
    if (downlink !== null) {
      values.push(`Débit estimé : ${downlink} Mb/s`);
    }

    const browserRtt = formatMetric(network.rtt);
    if (browserRtt !== null) {
      values.push(`Latence estimée : ${browserRtt} ms`);
    }

    const probeLatency = formatMetric(network.probeLatencyMs);
    if (probeLatency !== null) {
      values.push(`Test ELSA : ${probeLatency} ms`);
    }

    return values;
  }, [network, showMetrics, showRecovered]);

  const isDismissed = dismissedStatus === network.status;
  const activeMessage = showRecovered
    ? {
        level: "success",
        title: "Connexion rétablie",
        description:
          "ELSA GESTION est de nouveau joignable. Vous pouvez poursuivre votre travail.",
      }
    : message;
  const isBannerVisible = Boolean(
    activeMessage && (!isDismissed || showRecovered)
  );

  useEffect(() => {
    if (!isBannerVisible) {
      return undefined;
    }

    document.body.classList.add("network-status-visible");
    return () => document.body.classList.remove("network-status-visible");
  }, [isBannerVisible]);

  if (!isBannerVisible || !activeMessage) {
    return null;
  }

  const { level, title, description } = activeMessage;
  const canDismiss =
    showRecovered || network.status !== CONNECTION_STATES.OFFLINE;
  const Icon = showRecovered
    ? CheckCircle2
    : network.status === CONNECTION_STATES.DEGRADED
      ? Gauge
      : WifiOff;

  return (
    <aside
      className={`network-status-banner network-status-banner--${level}${
        floating ? " network-status-banner--floating" : ""
      }`}
      data-testid="network-status-banner"
    >
      <span className="network-status-banner__icon" aria-hidden="true">
        <Icon size={21} strokeWidth={2.2} />
      </span>

      <div className="network-status-banner__content">
        <div
          className="network-status-banner__announcement"
          role={level === "error" ? "alert" : "status"}
          aria-live={level === "error" ? "assertive" : "polite"}
          aria-atomic="true"
        >
          <strong className="network-status-banner__title">{title}</strong>
          <span className="network-status-banner__description">
            {description}
          </span>
        </div>

        {metrics.length > 0 && (
          <span className="network-status-banner__metrics">
            {metrics.map((metric) => (
              <span key={metric}>{metric}</span>
            ))}
          </span>
        )}
      </div>

      <div className="network-status-banner__actions">
        {!showRecovered && (
          <button
            type="button"
            className="network-status-banner__retry"
            onClick={() => void network.checkNow()}
            disabled={network.isChecking}
          >
            <RefreshCw
              size={15}
              className={network.isChecking ? "network-status-banner__spin" : ""}
              aria-hidden="true"
            />
            {network.isChecking ? "Vérification..." : "Vérifier maintenant"}
          </button>
        )}

        {canDismiss && (
          <button
            type="button"
            className="network-status-banner__close"
            onClick={() => {
              if (showRecovered) {
                setShowRecovered(false);
              } else {
                setDismissedStatus(network.status);
              }
            }}
            aria-label="Masquer l’indicateur de connexion"
          >
            <X size={17} aria-hidden="true" />
          </button>
        )}
      </div>
    </aside>
  );
};

export default NetworkStatusBanner;
